<template lang="pug">
.generic-wrapper
  .container
    BackButton(name="StatisticsOverview")
    h1.title Eigentümerstatistiken
    .sub-headline Hier können Sie alle Eigentümerstatistiken sehen.
    
    .calendar-config.margin-top      
      b-field(group)
        b-field(label-position="on-border" label="Eigentümer")
          b-select(v-model="ownerId")
            option(v-for="owner in owners" :value="owner.id") {{owner.fullName}}
        .spacer.md
        b-field(label-position="on-border" label="Jahr")
          b-select(v-model="year")
            option(v-for="y in currentYear+10-rootYear") {{rootYear + y}}
        template(v-if="ownerId")
          .spacer.md
          b-button(type="is-light" icon-left="file-excel" @click="createExcel()" ) Mietobjektstatistik-Excel herunterladen

    .card.margin-top
      .main
        b-table(:data="ownerData"
                default-sort-direction="asc"
                default-sort="startDate"
                hoverable)
          b-table-column(field="rentObject.code" label='Objekt' v-slot="props")
            | {{props.row.rentObject.code}}: {{props.row.rentObject.name}}
          b-table-column(field="contractCount" label='Verträge' v-slot="props")
            | {{props.row.contractCount}}
          b-table-column(field="nightCount" label='Nächte' v-slot="props")
            | {{props.row.nightCount}}
          b-table-column(field="blockedCount" label='Sperrung' v-slot="props")
            | {{props.row.blockedCount}}
          b-table-column(field="grossRevenue" label='Brutto' v-slot="props")
            | {{props.row.grossRevenue / 100 | toCurrency}}
          b-table-column(field="totalCommission" label='Provision' v-slot="props")
            | {{props.row.totalCommission / 100 | toCurrency}}
          b-table-column(field="netRevenue" label='Netto' v-slot="props")
            | {{props.row.netRevenue / 100 | toCurrency}}
          template(#footer)
            th
              .th-wrap Summe 
            th
              .th-wrap {{sumData.contractCountSum}}
            th
              .th-wrap {{sumData.nightCountSum}}
            th
              .th-wrap {{sumData.blockedCountSum}}
            th
              .th-wrap {{sumData.grossRevenueSum/100 | toCurrency}}
            th
              .th-wrap {{sumData.totalCommissionSum/100 | toCurrency}}
            th
              .th-wrap {{sumData.netRevenueSum/100 | toCurrency}}

</template>

<script>
import { DateTime } from 'luxon';
import * as ExcelJS from 'exceljs';

export default {
  data() {
    return {
      ownerId: null,
      rootYear: new Date().getFullYear()-10,
      currentYear: new Date().getFullYear(),
      year: new Date().getFullYear(),
      ownerData: []
    };
  },
  computed: {
    owners() {
      return this.$store.state.owners.all;
    },
    sumData() {
      let contractCountSum = 0;
      let nightCountSum = 0;
      let blockedCountSum = 0;
      let grossRevenueSum = 0;
      let totalCommissionSum = 0;
      let netRevenueSum = 0;
      this.ownerData.forEach(o => {
        contractCountSum += o.contractCount;
        nightCountSum += o.nightCount;
        blockedCountSum += o.blockedCount;
        grossRevenueSum += o.grossRevenue;
        totalCommissionSum += o.totalCommission;
        netRevenueSum += o.netRevenue;
      });
      return {contractCountSum, nightCountSum, blockedCountSum, grossRevenueSum, totalCommissionSum, netRevenueSum}
    }
  },
  watch: {
    async ownerId() {
      this.ownerData = await this.$store.dispatch("owners/statistics", {id: this.ownerId, year: this.year});
    },
    async year() {
      this.ownerData = await this.$store.dispatch("owners/statistics", {id: this.ownerId, year: this.year});
    },
  },
  async mounted() {
    this.$store.dispatch("owners/findAll");
  },
  methods: {
    formatDate(d) {
      const dt = DateTime.fromISO(d);
      return dt.setLocale('de')
        .toFormat("dd.LL.yy")
    },
    formatDateTime(d) {
      if (d instanceof Date) {
        const dt = DateTime.fromJSDate(d);
        return dt.setLocale('de')
          .toFormat("dd.LL.yyyy")  
      }
      const dt = DateTime.fromISO(d);
      return dt.setLocale('de')
        .toFormat("dd.LL.yyyy")
    },
    toCurrency(value) {
      if (typeof value !== "number") {
          return value;
      }
      var formatter = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2
      });
      return formatter.format(value);
    },
    createExcel() {
      const wb = new ExcelJS.Workbook();

      let rootWs =  wb.addWorksheet('Eigentümerstatistik', {
        pageSetup:{
          paperSize: 9,
          orientation:'landscape',
          horizontalCentered: true,
          margins: {
            left: 0.2, right: 0.2,
            top: 0.2, bottom: 0.2,
            header: 0.1, footer: 0.1
          }
        }
      });
      this.generateWorksheet(rootWs, this.ownerData, this.owners.find(r => r.id === this.ownerId).fullName + " " + this.year);

      const self = this;
      wb.xlsx.writeBuffer( {
          base64: true
      })
      .then( function (xls64) {
          // build anchor tag and attach file (works in chrome)
          var a = document.createElement("a");
          var data = new Blob([xls64], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

          var url = URL.createObjectURL(data);
          a.href = url;
          a.download = `Eigentümerstatistik-${self.owners.find(r => r.id === self.ownerId).fullName + "-" + self.year}.xlsx`;
          document.body.appendChild(a);
          a.click();
          setTimeout(function() {
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
              },
              0);
      })
      .catch(function(error) {
          console.log(error.message);
      });
    },
    generateWorksheet(ws, values, name) {
      const fontOptions = {
         name: 'Arial',
         size: 9
      };
      const columns = [
        { header: 'Pos.', key: 'nr', width: 4, style: { font: fontOptions}},
        { header: 'Objekt', key: 'objectName', width: 22, style: { font: fontOptions}},
        { header: 'Verträge', key: 'contractCount', width: 14, style: { font: fontOptions}},
        { header: 'Nächte', key: 'nightCount', width: 14, style: { font: fontOptions}},
        { header: 'Sperrung', key: 'blockedCount', width: 14, style: { font: fontOptions}},
        { header: 'Brutto', key: 'grossRevenue', width: 12, style: { font: fontOptions}},
        { header: 'Prov.', key: 'totalCommission', width: 12, style: { font: fontOptions}},
        { header: 'Netto', key: 'netRevenue', width: 12, style: { font: fontOptions}}
      ];
      const border = {
        top: {style:'thin', color: {argb:'FFAAAAAA'}},
        left: {style:'thin', color: {argb:'FFAAAAAA'}},
        bottom: {style:'thin', color: {argb:'FFAAAAAA'}},
        right: {style:'thin', color: {argb:'FFAAAAAA'}}
      };
      ws.columns = columns;
      
      const rowOffset = 3;
      ws.duplicateRow(1, 2, true);
      ws.getRow(1).values = [name];
      ws.getRow(1).height = 25;
      ws.mergeCells('A1:H1');

      ws.getRow(2).values = [''];
      ws.getCell('A2').value = `Objekte: ${this.ownerData.length}`;
      ws.getCell('H2').value = `erstellt am: ${this.formatDateTime(new Date())}`;
      ws.getCell('H2').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.pageSetup.printTitlesColumn = 'A:H';
      ws.pageSetup.printTitlesRow = '1:3';

      // Here we add the data
      for (let i = 0; i < values.length; i+= 1) {
        const a = values[i];
        ws.insertRow(i+rowOffset+1, [
          i+1,
          `${a.rentObject.code}: ${a.rentObject.name}`,
          a.contractCount,
          a.nightCount,
          a.blockedCount,
          a.grossRevenue/100,
          a.totalCommission/100,
          a.netRevenue/100
        ]);
      }

      // Summe
      ws.insertRow(values.length+rowOffset+1, [
        '',
        '',
        this.sumData.contractCountSum,
        this.sumData.nightCountSum,
        this.sumData.blockedCountSum,
        this.sumData.grossRevenueSum/100,
        this.sumData.totalCommissionSum/100,
        this.sumData.netRevenueSum/100
      ]);
      ws.getRow(values.length+rowOffset+1).font = { ...fontOptions, bold: true };

      // Custom Color Styles
      // ws.getColumn('isChange').font = { ...fontOptions, bold: true, color: { argb: 'FFFF0000' } };
      // ws.getColumn('bw').font = { ...fontOptions, bold: true, color: { argb: 'FFFF0000' } };
      // ws.getColumn('ht').font = { ...fontOptions, bold: true, color: { argb: 'FFFF0000' } };
      // ws.getColumn('kb').font = { ...fontOptions, bold: true, color: { argb: 'FFFF0000' } };
      // ws.getColumn('kh').font = { ...fontOptions, bold: true, color: { argb: 'FFFF0000' } };
      // ws.getColumn('paymentLeft').font = { ...fontOptions, bold: true, color: { argb: 'FFFF0000' } };
      ws.getRow(1).font = { ...fontOptions, bold: true, color: { argb: 'FF000000' }, size: 14 };
      ws.getRow(2).font = { ...fontOptions, bold: true, color: { argb: 'FF000000' } };
      ws.getRow(3).font = { ...fontOptions, bold: true };

      // Here we align everything and calculate row height
      ws.eachRow(function (row, rowNr) {
        if (rowNr <= 2) return;
        let maxHeight = 0;
        row.eachCell(function (cell) {
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = border;
            let maxHeightTemp = cell.value.toString().split('\n').length * 25;
            if (maxHeight < maxHeightTemp) maxHeight = maxHeightTemp;
        });
        if (rowNr >= 4) {
          row.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
          row.height = maxHeight < 25 ? 25 : maxHeight;
        }
      });

      // Custom Alignment Styles
      // ws.getColumn('lastName').alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      // ws.getColumn('infos').alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
      // ws.getColumn('totalPrice').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.getColumn('grossRevenue').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.getColumn('totalCommission').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.getColumn('netRevenue').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.getColumn('grossRevenue').numFmt = '#,##0.00 €';
      ws.getColumn('totalCommission').numFmt = '#,##0.00 €';
      ws.getColumn('netRevenue').numFmt = '#,##0.00 €';
      ws.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      ws.getRow(3).alignment = { vertical: 'middle', horizontal: 'center' };

      ws.autoFilter = `A$${rowOffset}:H${rowOffset+values.length}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-text {
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.extra-table {
  font-size: 0.8rem;
  td {
    padding: 0;
  }
}
</style>